import React from 'react';
import type { EventLogEntry } from '../../types/council';
import { logTargetToTurnKey } from './logTargetToTurnKey';
import { categoryOf } from './expeditionLogReducer';

const GLYPH: Record<string, string> = {
  trail_forked: '⑂', merge_completed: '⊕', artifact_created: '◆',
  cascade_depth_exceeded: '⚠', budget_overshoot: '💰', turn_added: '•',
  peer_invited: '＋', peer_dismissed: '－', hill_retargeted: '⛰',
  governor_proposed: '⚖', governor_recommended: '◇',
  governor_applied: '✓', governor_dismissed: '✕',
};

const TRIGGER_ICON: Record<string, string> = {
  budget_overshoot: '💰', cascade_depth_exceeded: '🌀', sweep_cost_trending: '📈',
};

function hhmm(ts: string): string {
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 5);
}

export const ExpeditionLogEntry: React.FC<{
  entry: EventLogEntry; onFocus: (turnKey: string) => void; darkMode: boolean;
  actionable?: boolean; inFlight?: boolean;
  onApprove?: () => void; onDismiss?: () => void;
}> = ({ entry, onFocus, darkMode, actionable, inFlight, onApprove, onDismiss }) => {
  const turnKey = logTargetToTurnKey(entry);
  const gov = categoryOf(entry.event_type) === 'governance';
  return (
    <div
      role="group"
      className={`flex gap-2 items-start px-2 py-1 text-[11px] rounded ${
        darkMode ? 'text-gray-200' : 'text-gray-700'
      }`}
    >
      <button
        type="button"
        data-testid="exp-log-row"
        onClick={() => turnKey && onFocus(turnKey)}
        className={`flex-1 min-w-0 text-left flex gap-2 items-start ${
          turnKey ? 'cursor-pointer hover:bg-black/5 dark:hover:bg-white/10' : 'cursor-default'
        }`}
      >
        <span className="opacity-60 tabular-nums shrink-0">{hhmm(entry.timestamp)}</span>
        <span className={`shrink-0 ${gov ? 'text-red-500 dark:text-red-400' : 'opacity-70'}`}>
          {GLYPH[entry.event_type] || '·'}
        </span>
        <span className="min-w-0 flex flex-col">
          <span className="break-words">{entry.summary}</span>
          {gov && (entry.target as any)?.reason && (
            <span data-testid="gov-reason" className="mt-0.5 text-[10px] opacity-70 break-words">
              {TRIGGER_ICON[(entry.target as any)?.trigger_type] ?? '↳'} {(entry.target as any).reason}
            </span>
          )}
        </span>
      </button>
      {actionable && (
        <span className="shrink-0 flex gap-1">
          <button
            type="button"
            disabled={inFlight}
            onClick={(e) => { e.stopPropagation(); onApprove?.(); }}
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
              darkMode ? 'bg-emerald-700/40 text-emerald-200 hover:bg-emerald-700/60'
                       : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            Approve
          </button>
          <button
            type="button"
            disabled={inFlight}
            onClick={(e) => { e.stopPropagation(); onDismiss?.(); }}
            className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
              darkMode ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700/70'
                       : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            Dismiss
          </button>
        </span>
      )}
    </div>
  );
};
